<template>
  <div id="dashboard-page" class="application-page">
    <!-- Company selector -->
    <div class="toolbar-container">
      <div class="toolbar" v-show="companies.length > 1">
        <label for="company-select">{{ $t('dashboard.company') }}:</label>
        <select v-model="companyId" class="select-s2 browser-default small-contents">
          <option v-for="company in companies" :key="company.Item1" :value="company.Item1">
            {{ company.Item2 }}
          </option>
        </select>
      </div>
    </div>

    <!-- Container for split-page sections -->
    <div class="split-page-container">
      <!-- Left side (last status) -->
      <div class="split-page z-depth-2 left-tab-page-fixed">
        <dashboard-laststatus
          :lastUpdated="lastUpdated"
          :companyId="companyId"
          :companyName="companyName"
          @updating="handleUpdating">
        </dashboard-laststatus>
      </div>

      <!-- Right side (tabs: map and incidents) -->
      <div class="split-page z-depth-2 right-tab-page-fixed">
        <div class="tab-content">
          <div class="tab-bar">
            <div @click="switchTab('incidents')" class="tab clickable" :class="{'active': tab === 'incidents'}">{{ $t('dashboard.incidents')}}</div>
            <div @click="switchTab('map')" class="tab clickable" :class="{'active': tab === 'map'}">{{ $t('dashboard.map') }}</div>
          </div>
          <div class="tab-page">
            <dashboard-map
              ref="map"
              :railObjects="railObjects"
              :lastUpdated="lastUpdated"
              :companyId="companyId"
              v-if="tab === 'map'"
              style="height: 100%; width: 100%;"
            />
            <dashboard-incidents
              ref="incidents"
              :companyId="companyId"
              :railObjects="railObjects"
              v-if="tab === 'incidents'"
              style="height: 100%; width: 100%;"
              @updating="handleUpdating"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthenticationService from '@/services/AuthenticationService'
import DashboardService from '@/services/DashboardService'
import RailObjectService from '@/services/RailObjectService'
import LastStatus from './_LastStatus.vue'
import Incidents from './_Incidents.vue'
import DashboardMap from './DashboardMap'

export default {
  name: 'dashboard',
  components: {
    'dashboard-laststatus': LastStatus,
    'dashboard-map': DashboardMap,
    'dashboard-incidents': Incidents
  },
  data () {
    return {
      currentUser: AuthenticationService.getUser(),
      tab: 'map',
      railObjects: [],
      companies: [],
      lastUpdated: new Date(),
      companyId: null
    }
  },
  computed: {
    companyName () {
      const company = this.companies.find(c => c.Item1 === this.companyId)
      return company ? company.Item2 : 'Unknown'
    }
  },
  async created () {
    try {
      const response = await DashboardService.getCompanies(this.currentUser.UserId)
      this.companies = response.data

      this.companies.unshift({ Item1: null, Item2: 'None' })

      const storedCompanyId = Number(localStorage.getItem('selectedCompanyId'))
      const companyExists = this.companies.some(company => company.Item1 === storedCompanyId)

      this.companyId = companyExists
        ? storedCompanyId
        : (this.companies.some(company => company.Item1 === this.currentUser.CompanyId)
          ? this.currentUser.CompanyId
          : null)

      this.railObjects = RailObjectService.getRailObjects()
      this.lastUpdated = new Date()
    } catch (error) {
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.handleTabChange(this.tab)
    })
  },
  watch: {
    companyId (newVal) {
      localStorage.setItem('selectedCompanyId', newVal)
    },
    tab (newTab) {
      this.handleTabChange(newTab)
    }
  },
  methods: {
    switchTab (newTab) {
      this.tab = newTab
    },
    handleTabChange (newTab) {
      if (newTab === 'incidents') {
        this.$nextTick(() => {
          const incidentsComponent = this.$refs.incidents
          if (incidentsComponent) {
            incidentsComponent.updateRowsPerPage()
          }
        })
      } else if (newTab === 'map') {
        this.$nextTick(() => {
          const mapComponent = this.$refs.map
          if (mapComponent) {
            mapComponent.initializeMap()
          }
        })
      }
    },
    handleUpdating () {
      this.lastUpdated = new Date()
    }
  }
}
</script>

<style scoped>
#dashboard-page {
  width: 100%;
  height: calc(100vh - 56px);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  z-index: 0;
}

.toolbar-container {
  width: 100%;
  background-color: white;
  border-bottom: 1px solid #ccc;
  z-index: 100;
}

.toolbar {
  padding: 5px 8px;
  display: flex;
  align-items: center;
  height: 35px;
  font-size: 12px;
}

.toolbar label {
  margin-right: 5px;
  font-size: 12px;
}

.toolbar select {
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  height: 24px;
  width: auto;
  min-width: 50px;
  max-width: 100%;
  pointer-events: auto;
}

.split-page-container {
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  width: 100%;
  height: calc(100vh - 91px);
  min-height: 0;
}

.split-page {
  margin: 10px;
  width: calc(50% - 20px);
  height: calc(100% - 20px);
  background-color: #fff;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
  z-index: 5;
}

.left-tab-page-fixed,
.right-tab-page-fixed {
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Ensure scroll events reach the map */
}

.tab-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tab-bar {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 40px;
  z-index: 1000;
  position: relative;
  background-color: #f4f4f4;
}

.tab-content .tab {
  width: 50%;
  display: inline-block;
  text-align: center;
  padding: 10px 0;
  color: #56CCF2;
  cursor: pointer;
  pointer-events: auto;
  position: relative;
  z-index: 1001;
}

.tab-page {
  position: relative;
  width: 100%;
  height: calc(100% - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tab.active::after {
  content: '';
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  border-bottom: 3px solid #56CCF2;
}

.clickable,
select,
button,
input,
label,
table,
th,
td,
tr {
  pointer-events: auto !important;
  z-index: 10;
}

.tab-page .map-container {
  z-index: 1;
}
</style>



// WEBPACK FOOTER //
// src/pages/dashboard2/Dashboard.vue